// TarifReadOnlyGrid — rendu en TABLE HTML pure (pas d'inputs) du tarif
// applique a un contact. Utilise dans :
//   - l'onglet Tarif de la fiche view
//   - le popup "Voir le tarif de base" / "Voir le tarif" depuis la fiche edit
//
// Surligne (ambre) la LIGNE entiere quand au moins une valeur de la ligne
// differe du tarif standard (reference). Le libelle de la ligne est aussi
// mis en surbrillance pour bien identifier la ligne modifiee.
import React from 'react';

const BORNE_TYPES_RO = [
  { key: 'spherik', label: 'Spherik', color: '#ff0fa4' },
  { key: 'classik', label: 'Classik', color: '#0ea5e9' },
  { key: 'prestige', label: 'Prestige', color: '#dc2626' },
];

const fmtEuro = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

const isDiff = (a, b) => {
  if (b === undefined || b === null) return false;
  const x = Number(a ?? 0);
  const y = Number(b ?? 0);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return Math.abs(x - y) > 0.0001;
};

// Cell renderer for a single value. The cell is highlighted only when the
// row is highlighted (handled via rowHighlight prop coming from the parent
// row), and the value itself is the override.
function TarifCell({ value, stdValue, rowHighlight }) {
  const override = isDiff(value, stdValue);
  return (
    <td style={{
      padding: '6px 10px',
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
      background: rowHighlight ? '#fef3c7' : 'transparent',
      color: override ? '#92400e' : (rowHighlight ? '#92400e' : '#0f172a'),
      fontWeight: override ? 700 : (rowHighlight ? 600 : 500),
      borderBottom: '1px solid #f1f5f9',
    }} title={override && stdValue !== undefined ? `Standard : ${fmtEuro(stdValue)}` : undefined}>
      {fmtEuro(value)}
    </td>
  );
}

const EMPTY_CELL = (k, rowHighlight) => (
  <td key={k} style={{ textAlign: 'right', padding: '6px 10px', color: '#cbd5e1', background: rowHighlight ? '#fef3c7' : 'transparent', borderBottom: '1px solid #f1f5f9' }}>—</td>
);

function ByTypeReadRow({ label, values, refValues, global }) {
  // Row-level highlight if ANY type cell differs from the standard.
  const rowOverride = BORNE_TYPES_RO.some((t) => isDiff(values?.[t.key], refValues?.[t.key]));
  return (
    <tr style={rowOverride ? { background: '#fef3c7' } : undefined}>
      <td style={{
        padding: '6px 10px',
        fontSize: 13,
        color: rowOverride ? '#92400e' : '#475569',
        fontWeight: rowOverride ? 700 : 400,
        borderBottom: '1px solid #f1f5f9',
      }}>{label}</td>
      {BORNE_TYPES_RO.map((t) => (
        <TarifCell key={t.key} value={values?.[t.key]} stdValue={refValues?.[t.key]} rowHighlight={rowOverride} />
      ))}
      {global && EMPTY_CELL('g', rowOverride)}
    </tr>
  );
}

// A "global" row: the value is not per borne type → it lives in the Global column.
function ScalarRow({ label, value, stdValue, global }) {
  const rowOverride = isDiff(value, stdValue);
  return (
    <tr style={rowOverride ? { background: '#fef3c7' } : undefined}>
      <td style={{
        padding: '6px 10px',
        fontSize: 13,
        color: rowOverride ? '#92400e' : '#475569',
        fontWeight: rowOverride ? 700 : 400,
        borderBottom: '1px solid #f1f5f9',
      }}>{label}</td>
      {global ? (
        <>
          {BORNE_TYPES_RO.map((t) => EMPTY_CELL(t.key, rowOverride))}
          <TarifCell value={value} stdValue={stdValue} rowHighlight={rowOverride} />
        </>
      ) : (
        <td colSpan={BORNE_TYPES_RO.length} style={{ padding: 0, borderBottom: '1px solid #f1f5f9' }}>
          <TarifCell value={value} stdValue={stdValue} rowHighlight={rowOverride} />
        </td>
      )}
    </tr>
  );
}

function TarifTable({ title, rows, global }) {
  return (
    <div style={{ marginBottom: 18, border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: '#f8fafc', fontWeight: 700, fontSize: 13, color: '#0f172a', borderBottom: '1px solid #e2e8f0' }}>{title}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
        <thead>
          <tr style={{ background: '#fafbfc' }}>
            <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.04, borderBottom: '1px solid #e2e8f0' }}>Libellé</th>
            {BORNE_TYPES_RO.map((t) => (
              <th key={t.key} style={{ textAlign: 'right', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: t.color, textTransform: 'uppercase', letterSpacing: 0.04, borderBottom: '1px solid #e2e8f0' }}>{t.label}</th>
            ))}
            {global && (
              <th style={{ textAlign: 'right', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: 0.04, borderBottom: '1px solid #e2e8f0' }}>Global</th>
            )}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

export default function TarifReadOnlyGrid({ data, reference }) {
  if (!data) return null;
  const r = reference || {};
  return (
    <div>
      <TarifTable title="Missions pickup" rows={[
        <ByTypeReadRow key="pk" label="Forfait pickup" values={data.pickup} refValues={r.pickup} />,
      ]} />

      <TarifTable title="Missions installations / livraisons" global rows={[
        <ScalarRow key="km" global label="Barème kilométrique (km par défaut : 1)" value={data.livraison?.bareme_km} stdValue={r.livraison?.bareme_km} />,
        <ScalarRow key="mkm" global label="Forfait minimum kilométrique" value={data.livraison?.forfait_min_km} stdValue={r.livraison?.forfait_min_km} />,
        <ByTypeReadRow key="al" global label="Forfait livraison aller" values={data.livraison?.forfait_aller} refValues={r.livraison?.forfait_aller} />,
        <ByTypeReadRow key="rt" global label="Forfait livraison retour" values={data.livraison?.forfait_retour} refValues={r.livraison?.forfait_retour} />,
        <ByTypeReadRow key="in" global label="Forfait installation" values={data.livraison?.forfait_installation} refValues={r.livraison?.forfait_installation} />,
        <ByTypeReadRow key="de" global label="Forfait désinstallation" values={data.livraison?.forfait_desinstallation} refValues={r.livraison?.forfait_desinstallation} />,
        <ByTypeReadRow key="alm" global label="Forfait livraison aller majoré" values={data.livraison?.forfait_aller_majore} refValues={r.livraison?.forfait_aller_majore} />,
        <ByTypeReadRow key="rtm" global label="Forfait livraison retour majoré" values={data.livraison?.forfait_retour_majore} refValues={r.livraison?.forfait_retour_majore} />,
        <ByTypeReadRow key="inm" global label="Forfait installation majoré" values={data.livraison?.forfait_installation_majore} refValues={r.livraison?.forfait_installation_majore} />,
        <ByTypeReadRow key="dem" global label="Forfait désinstallation majoré" values={data.livraison?.forfait_desinstallation_majore} refValues={r.livraison?.forfait_desinstallation_majore} />,
        <ScalarRow key="ma" global label="Forfait minimum livraison par trajet aller" value={data.livraison?.forfait_min_aller} stdValue={r.livraison?.forfait_min_aller} />,
        <ScalarRow key="mr" global label="Forfait minimum livraison par trajet retour" value={data.livraison?.forfait_min_retour} stdValue={r.livraison?.forfait_min_retour} />,
      ]} />

      <TarifTable title="Animations spécifiques" global rows={[
        <ScalarRow key="fv1" global label="Forfait installation fond vert / photocall" value={data.animation?.installation_fond_vert} stdValue={r.animation?.installation_fond_vert} />,
        <ScalarRow key="fv2" global label="Forfait désinstallation fond vert / photocall" value={data.animation?.desinstallation_fond_vert} stdValue={r.animation?.desinstallation_fond_vert} />,
      ]} />

      {reference && (
        <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
          <span style={{ display: 'inline-block', width: 10, height: 10, background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 2, marginRight: 6, verticalAlign: 'middle' }} />
          Lignes en ambre : valeur personnalisée pour ce contact (différente du tarif standard).
        </p>
      )}
    </div>
  );
}

// Helper exporte separement : liste des chemins (path -> {label, current, std})
// modifies par rapport au standard. Utilise pour afficher la "liste des tarifs
// modifies" sur la fiche edit en mode tarif specifique.
export function listOverrides(data, reference) {
  if (!data || !reference) return [];
  const out = [];
  const pushIfDiff = (label, cur, std) => {
    if (isDiff(cur, std)) out.push({ label, current: cur, std });
  };
  const pushByType = (label, curObj, stdObj) => {
    for (const t of BORNE_TYPES_RO) {
      const cur = curObj?.[t.key];
      const std = stdObj?.[t.key];
      if (isDiff(cur, std)) {
        out.push({ label: `${label} (${t.label})`, current: cur, std });
      }
    }
  };

  // Pickup
  pushByType('Forfait pickup', data.pickup, reference.pickup);

  // Livraison
  pushIfDiff('Barème kilométrique', data.livraison?.bareme_km, reference.livraison?.bareme_km);
  pushByType('Forfait livraison aller', data.livraison?.forfait_aller, reference.livraison?.forfait_aller);
  pushByType('Forfait livraison retour', data.livraison?.forfait_retour, reference.livraison?.forfait_retour);
  pushByType('Forfait installation', data.livraison?.forfait_installation, reference.livraison?.forfait_installation);
  pushByType('Forfait désinstallation', data.livraison?.forfait_desinstallation, reference.livraison?.forfait_desinstallation);
  pushByType('Forfait livraison aller majoré', data.livraison?.forfait_aller_majore, reference.livraison?.forfait_aller_majore);
  pushByType('Forfait livraison retour majoré', data.livraison?.forfait_retour_majore, reference.livraison?.forfait_retour_majore);
  pushByType('Forfait installation majoré', data.livraison?.forfait_installation_majore, reference.livraison?.forfait_installation_majore);
  pushByType('Forfait désinstallation majoré', data.livraison?.forfait_desinstallation_majore, reference.livraison?.forfait_desinstallation_majore);
  pushIfDiff('Forfait minimum livraison aller', data.livraison?.forfait_min_aller, reference.livraison?.forfait_min_aller);
  pushIfDiff('Forfait minimum livraison retour', data.livraison?.forfait_min_retour, reference.livraison?.forfait_min_retour);

  // Animation
  pushIfDiff('Forfait installation fond vert / photocall', data.animation?.installation_fond_vert, reference.animation?.installation_fond_vert);
  pushIfDiff('Forfait désinstallation fond vert / photocall', data.animation?.desinstallation_fond_vert, reference.animation?.desinstallation_fond_vert);

  return out;
}

// Helper exporte pour le formatage des euros (utilise par les consommateurs).
export { fmtEuro };
